import { Navigate, Route, Routes } from 'react-router-dom'

import { PaginaIngreso } from '@/modules/auth/PaginaIngreso'
import { RutaProtegida } from '@/modules/auth/RutaProtegida'
import { PaginaChatbot } from '@/modules/chatbot/PaginaChatbot'
import { PaginaClientesWorldOffice } from '@/modules/comercial/PaginaClientesWorldOffice'
import { PaginaInventario } from '@/modules/comercial/PaginaInventario'
import { PaginaCotizaciones } from '@/modules/cotizaciones/PaginaCotizaciones'
import { PaginaFacturas } from '@/modules/facturas/PaginaFacturas'
import { PaginaCompras } from '@/modules/compras/PaginaCompras'
import { PaginaClientes } from '@/modules/maestros/clientes/PaginaClientes'
import { PaginaBodega } from '@/modules/maestros/bodega/PaginaBodega'
import { PaginaColaboradores } from '@/modules/maestros/colaboradores/PaginaColaboradores'
import { PaginaHerramientas } from '@/modules/maestros/herramientas/PaginaHerramientas'
import { PaginaServicios } from '@/modules/maestros/servicios/PaginaServicios'
import { DetalleOrdenServicio } from '@/modules/ordenes-servicio/DetalleOrdenServicio'
import { MatrizOrdenes } from '@/modules/ordenes-servicio/MatrizOrdenes'
import { PaginaServiciosFinalizados } from '@/modules/servicios-finalizados/PaginaServiciosFinalizados'
import { PaginaMiTrabajo } from '@/modules/campo/PaginaMiTrabajo'
import { PaginaMiProgramacion } from '@/modules/campo/PaginaMiProgramacion'
import { PaginaMiHistorial } from '@/modules/campo/PaginaMiHistorial'
import { PaginaDevolver } from '@/modules/campo/PaginaDevolver'
import { PaginaEtiquetas } from '@/modules/herramientas/etiquetas/PaginaEtiquetas'
import { PaginaAuditoria } from '@/modules/auditoria/PaginaAuditoria'
import { PaginaAlistamientos } from '@/modules/bodega/PaginaAlistamientos'
import { PaginaSobrantes } from '@/modules/sobrantes/PaginaSobrantes'
import { PaginaCartera } from '@/modules/financiera/PaginaCartera'
import { DondeEmpezar } from './DondeEmpezar'

import { ArmazonApp } from './layout/ArmazonApp'

/**
 * Rutas del sistema, en el orden del menú.
 *
 * Las secciones cotizadas que todavía no se construyen quedan como marcadores
 * con su número: el sistema completo se puede recorrer desde el primer día y
 * el avance se mide contra lo que se vendió.
 */
export function Rutas() {
  return (
    <Routes>
      <Route path="/ingresar" element={<PaginaIngreso />} />

      <Route
        element={
          <RutaProtegida>
            <ArmazonApp />
          </RutaProtegida>
        }
      >
        {/* ── General ─────────────────────────────────────────────────── */}
        {/* Sin RutaProtegida: quien no tiene tablero no se queda mirando un
            «sin acceso», se le manda a donde sí tiene qué hacer. */}
        <Route index element={<DondeEmpezar />} />

        {/* La pantalla del técnico. Va bajo ordenes_trabajo, que es el único
            módulo de operación que su rol tiene: no hay que abrirle nada. */}
        <Route
          path="mi-trabajo"
          element={
            <RutaProtegida modulo="ordenes_trabajo">
              <PaginaMiTrabajo />
            </RutaProtegida>
          }
        />

        {/* Las otras dos pantallas del técnico (menú inferior). Mismo módulo
            que «Mi trabajo»: es lo único de operación que su rol tiene. */}
        <Route
          path="mi-programacion"
          element={
            <RutaProtegida modulo="ordenes_trabajo">
              <PaginaMiProgramacion />
            </RutaProtegida>
          }
        />
        <Route
          path="mi-historial"
          element={
            <RutaProtegida modulo="ordenes_trabajo">
              <PaginaMiHistorial />
            </RutaProtegida>
          }
        />

        {/* ── Operación ───────────────────────────────────────────────── */}
        <Route
          path="ordenes"
          element={
            <RutaProtegida modulo="ordenes_servicio">
              <MatrizOrdenes />
            </RutaProtegida>
          }
        />
        <Route
          path="ordenes/:id"
          element={
            <RutaProtegida modulo="ordenes_servicio">
              <DetalleOrdenServicio />
            </RutaProtegida>
          }
        />

        <Route
          path="chatbot"
          element={
            <RutaProtegida modulo="ordenes_trabajo">
              <PaginaChatbot />
            </RutaProtegida>
          }
        />

        {/* El catálogo de herramientas ERA la Sección 4 desde el principio: la
            primera mitad de ese módulo. Tenerlo además en Configuración eran
            dos entradas de menú para lo mismo, y una de las dos vacía. */}
        <Route
          path="herramientas"
          element={
            <RutaProtegida modulo="herramientas">
              <PaginaHerramientas />
            </RutaProtegida>
          }
        />

        {/* Las etiquetas piden 'editar' y no 'ver': llevan el código que abre
            la devolución. La base ya lo impide por su cuenta —al operario la
            consulta le devuelve cero filas— pero mandarlo a una pantalla que
            va a salir vacía es una puerta que se abre para nada. */}
        <Route
          path="herramientas/etiquetas"
          element={
            <RutaProtegida modulo="herramientas" accion="editar">
              <PaginaEtiquetas />
            </RutaProtegida>
          }
        />

        {/* Aquí se llega apuntando la cámara al QR de una herramienta, no
            desde el menú. Sin módulo: la puede abrir cualquiera que haya
            entrado, porque quien devuelve es el técnico y su rol no
            administra herramientas. Lo que decide es la geocerca. */}
        <Route path="devolver/:token" element={<PaginaDevolver />} />

        <Route
          path="servicios-finalizados"
          element={
            <RutaProtegida modulo="servicios_finalizados">
              <PaginaServiciosFinalizados />
            </RutaProtegida>
          }
        />

        {/* ── Comercial y Clientes ────────────────────────────────────── */}
        {/* Los clientes salen de World Office: son 2.566 y allá vive la
            contabilidad. La pantalla de maestros queda para los datos que la
            matriz agrega. */}
        <Route
          path="clientes"
          element={
            <RutaProtegida modulo="maestros">
              <PaginaClientesWorldOffice />
            </RutaProtegida>
          }
        />

        <Route
          path="inventario"
          element={
            <RutaProtegida modulo="maestros">
              <PaginaInventario />
            </RutaProtegida>
          }
        />

        <Route
          path="maestros/clientes-operacion"
          element={
            <RutaProtegida modulo="maestros">
              <PaginaClientes />
            </RutaProtegida>
          }
        />

        <Route
          path="cotizaciones"
          element={
            <RutaProtegida modulo="cotizaciones">
              <PaginaCotizaciones />
            </RutaProtegida>
          }
        />

        <Route
          path="facturas"
          element={
            <RutaProtegida modulo="cotizaciones">
              <PaginaFacturas />
            </RutaProtegida>
          }
        />

        {/* ── Financiera y Bodega (0246) ─────────────────────────────── */}
        <Route
          path="financiera/cartera"
          element={
            <RutaProtegida modulo="cartera">
              <PaginaCartera />
            </RutaProtegida>
          }
        />
        <Route
          path="bodega/alistamientos"
          element={
            <RutaProtegida modulo="bodega">
              <PaginaAlistamientos />
            </RutaProtegida>
          }
        />
        <Route
          path="sobrantes"
          element={
            <RutaProtegida modulo="sobrantes">
              <PaginaSobrantes />
            </RutaProtegida>
          }
        />

        {/* ── Auditoría (0249): solo lectura, solo gerencia ───────────── */}
        <Route
          path="auditoria"
          element={
            <RutaProtegida modulo="auditoria">
              <PaginaAuditoria />
            </RutaProtegida>
          }
        />

        <Route
          path="compras"
          element={
            <RutaProtegida modulo="compras">
              <PaginaCompras />
            </RutaProtegida>
          }
        />

        {/* ── Configuración ───────────────────────────────────────────── */}
        <Route
          path="maestros/servicios"
          element={
            <RutaProtegida modulo="maestros">
              <PaginaServicios />
            </RutaProtegida>
          }
        />
        <Route
          path="maestros/colaboradores"
          element={
            <RutaProtegida modulo="maestros">
              <PaginaColaboradores />
            </RutaProtegida>
          }
        />
        <Route
          path="maestros/bodega"
          element={
            <RutaProtegida modulo="maestros">
              <PaginaBodega />
            </RutaProtegida>
          }
        />
        <Route
          path="maestros/herramientas-viejo"
          element={
            <RutaProtegida modulo="maestros">
              <PaginaHerramientas />
            </RutaProtegida>
          }
        />

        {/* Las direcciones viejas siguen funcionando. */}
        <Route path="ordenes-servicio/*" element={<Navigate to="/ordenes" replace />} />
        <Route path="maestros/clientes" element={<Navigate to="/clientes" replace />} />
        <Route path="maestros/herramientas" element={<Navigate to="/herramientas" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
